import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./assest_design/PodProgramme.css";

import cardFront from "./assets/card1.png";
import cardBack from "./assets/card2.png";

export default function PodProgramme() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title =
      "Pod Programme | Lifetime Software Support & System Adoption | ProCode ZA";

    const setMeta = (name, content, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;

      let tag = document.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        if (property) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMeta(
      "description",
      "Join the ProCode ZA Pod Programme for long-term software support, system adoption, legacy takeover, feature upgrade discounts, architecture continuity and priority technical response for businesses in South Africa."
    );
    setMeta(
      "keywords",
      "software support South Africa, system maintenance South Africa, legacy system takeover, long-term software support, website support South Africa, platform support South Africa, technical support retainer, ProCode ZA Pod Programme"
    );
    setMeta(
      "og:title",
      "Pod Programme | Lifetime Software Support & System Adoption | ProCode ZA",
      true
    );
    setMeta(
      "og:description",
      "Long-term technical support, system adoption, legacy takeover, architecture continuity and feature discounts for businesses that need more than one-off development.",
      true
    );
    setMeta("og:type", "website", true);
  }, []);

  return (
    <div className="pod-page">
      {/* HERO */}
      <section className="pod-hero">
        <span className="pod-badge">Premium Membership</span>

        <h1>The Pod Programme</h1>

        <p className="pod-subtitle">
          Long-term software support, system adoption and technical continuity
          for businesses that do not want to be stranded after launch.
        </p>

        <div className="pod-price">
          <strong>R850 / month</strong>
          <span>No lock-in · Cancel anytime</span>
        </div>

        <div className="pod-hero-actions">
          <button
            className="pod-cta"
            onClick={() => navigate("/proquote")}
            type="button"
          >
            Get Your ProQuote
          </button>

          <button
            className="pod-cta ghost"
            onClick={() => navigate("/contact")}
            type="button"
          >
            Join the Pod
          </button>
        </div>

        <p className="pod-trust">
          Most clients understand the value of Pod after one emergency dev bill,
          one vanished developer or one expensive system failure.
        </p>

        <div className="pod-hero-pills">
          <span>Lifetime support direction</span>
          <span>Legacy system takeover</span>
          <span>Priority feature growth</span>
          <span>Real engineers, not generic support</span>
        </div>
      </section>

      {/* VALUE INTRO */}
      <section className="pod-section intro">
        <h2>What the Pod Programme actually is</h2>
        <p>
          The Pod Programme is ProCode ZA’s premium support and system adoption
          membership for businesses that need more than one-off development. It
          is designed for clients who want long-term technical continuity,
          support they can rely on, and a team that understands their platform
          over time.
        </p>
        <p>
          Whether we built your system or not, Pod gives your software a more
          stable technical future through support, oversight, structured
          continuity and a clearer path for upgrades.
        </p>
      </section>

      {/* PAIN */}
      <section className="pod-section">
        <h2>Why most software fails after launch</h2>
        <p className="pod-section-copy">
          Many businesses do not fail because the original build was impossible.
          They fail because no one stays responsible for the system after launch.
        </p>

        <ul className="pod-pain-list">
          <li>❌ The original developer disappears or becomes unavailable</li>
          <li>❌ No one properly understands the codebase anymore</li>
          <li>❌ Small changes become expensive, slow and stressful</li>
          <li>❌ There is no technical memory or architecture continuity</li>
          <li>❌ Existing systems become harder to trust and harder to grow</li>
        </ul>
      </section>

      {/* SOLUTION */}
      <section className="pod-section highlight">
        <h2>We do not just support your system. We adopt it.</h2>
        <p>
          The Pod Programme gives your software a technical home, whether we
          built it from scratch or stepped in later. That means less risk, less
          guesswork and more confidence when your business needs support,
          changes, upgrades or continuity.
        </p>
      </section>

      {/* VALUE GRID */}
      <section className="pod-grid">
        <PodCard
          title="Lifetime Support Direction"
          desc="Once your system is adopted into Pod, you have an ongoing technical relationship instead of one-off emergency assistance."
        />
        <PodCard
          title="Legacy System Takeover"
          desc="We can step into systems built by previous developers and provide structure, support and a clearer long-term path."
        />
        <PodCard
          title="Dedicated Pod Knowledge"
          desc="Your system is understood over time by a team that builds familiarity, not by a random ticket queue."
        />
        <PodCard
          title="Feature Upgrade Discounts"
          desc="Pod members receive better pricing on future improvements, new functionality and expansion work."
        />
        <PodCard
          title="Architecture Memory"
          desc="We document and retain context so your system is not dependent on one person remembering everything."
        />
        <PodCard
          title="Priority Technical Lane"
          desc="When issues matter, Pod members move faster through support and escalation than standard ad hoc requests."
        />
      </section>

      {/* WHO IT'S FOR */}
      <section className="pod-section">
        <h2>Who the Pod Programme is for</h2>
        <div className="pod-audience-grid">
          <div className="pod-audience-card">
            <h3>Startups & small businesses</h3>
            <p>
              Ideal for growing teams that cannot afford technical instability
              or expensive emergency rebuilds.
            </p>
          </div>

          <div className="pod-audience-card">
            <h3>SMBs & operational teams</h3>
            <p>
              Useful for businesses running internal systems, dashboards,
              booking tools, portals or client-facing platforms that need
              reliable long-term support.
            </p>
          </div>

          <div className="pod-audience-card">
            <h3>Institutions & larger operations</h3>
            <p>
              Strong fit for organisations that need continuity, accountability
              and more confidence around platform maintenance and upgrades.
            </p>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP CARDS */}
      <section className="pod-section certificate">
        <h2>Official Pod Membership</h2>

        <p className="certificate-subtext">
          Every Pod member receives an official digital membership card and
          certificate confirming that their website, system or platform is under
          active ProCode ZA technical care.
        </p>

        <div className="pod-card-display">
          <img src={cardFront} alt="Pod Membership Card Front" />
          <img src={cardBack} alt="Pod Membership Card Back" />
        </div>

        <p className="certificate-footnote">
          Each membership includes a unique member ID and verification
          reference.
        </p>
      </section>

      {/* WHY IT SELLS */}
      <section className="pod-section">
        <h2>Why businesses join Pod</h2>
        <p className="pod-section-copy">
          Pod is not just about support tickets. It is about protecting
          business continuity, reducing risk, improving response confidence and
          creating a more stable relationship between your business and the
          technology it depends on.
        </p>

        <div className="pod-benefits-list">
          <div className="pod-benefit-item">
            <strong>Less technical risk</strong>
            <span>
              Your system is not left exposed the moment the original project
              ends.
            </span>
          </div>

          <div className="pod-benefit-item">
            <strong>Better upgrade readiness</strong>
            <span>
              New features, changes and improvements become easier to plan and
              price.
            </span>
          </div>

          <div className="pod-benefit-item">
            <strong>More trustworthy support</strong>
            <span>
              You work with a team that already understands your environment.
            </span>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pod-section final">
        <h2>Start with a ProQuote</h2>

        <p>
          Tell us about your website, platform or system and we will show you
          where Pod fits, what it protects and how it supports your long-term
          technical future.
        </p>

        <button
          className="pod-cta large"
          onClick={() => navigate("/proquote")}
          type="button"
        >
          Get My ProQuote
        </button>

        <p className="pod-fineprint">
          Limited Pod capacity · Quality over quantity
        </p>
      </section>
    </div>
  );
}

function PodCard({ title, desc }) {
  return (
    <div className="pod-card">
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}